import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Award as AwardIcon, Medal, Trophy, Crown, Gem, PlaneTakeoff, Star } from "lucide-react";
import { getAwards } from "@/lib/api";

// Pick a distinctive icon + tier colour for each milestone based on its badge/title.
const getMilestone = (a) => {
  const badge = (a.badge || "").toUpperCase();
  const text = `${a.badge || ""} ${a.title || ""}`.toUpperCase();
  const num = parseInt((text.match(/\d[\d,]*/) || ["0"])[0].replace(/,/g, ""), 10) || 0;
  const isFlights = badge.includes("F") || /FLIGHT/.test(text);

  if (isFlights) {
    return { Icon: PlaneTakeoff, color: "#D4AF37", glow: "rgba(212,175,55,0.25)", label: "Flights" };
  }
  // Hours milestones — tiered
  if (num >= 7000) return { Icon: Gem, color: "#7FD4E6", glow: "rgba(127,212,230,0.3)", label: "Hours" };
  if (num >= 5000) return { Icon: Crown, color: "#F2C94C", glow: "rgba(242,201,76,0.3)", label: "Hours" };
  if (num >= 3000) return { Icon: Trophy, color: "#D4AF37", glow: "rgba(212,175,55,0.28)", label: "Hours" };
  if (num >= 1000) return { Icon: Medal, color: "#C0C0C0", glow: "rgba(192,192,192,0.25)", label: "Hours" };
  if (num >= 100) return { Icon: Star, color: "#D4AF37", glow: "rgba(212,175,55,0.22)", label: "Hours" };
  return { Icon: AwardIcon, color: "#CD7F32", glow: "rgba(205,127,50,0.28)", label: "Milestone" };
};

export const Awards = () => {
  const [awards, setAwards] = useState([]);

  useEffect(() => {
    getAwards().then(setAwards).catch(() => {});
  }, []);

  return (
    <section id="awards" data-testid="awards-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="mb-14 max-w-2xl">
          <p className="text-overline text-[#8A1538]">Recognition</p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[var(--aiv-text)] lg:text-4xl">
            Latest Awards & Milestones
          </h2>
          <p className="mt-4 text-base text-[var(--aiv-text-2)]">
            Dedication in the virtual skies deserves recognition. Celebrating the
            milestones our pilots recently unlocked.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {awards.map((a, i) => {
            const { Icon, color, glow } = getMilestone(a);
            return (
              <motion.div
                key={`${a.callsign}-${a.title}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`award-card-${a.callsign}`}
                className="group relative overflow-hidden rounded-2xl border border-white/5 bg-[var(--aiv-surface)] p-7 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="absolute right-6 top-6 opacity-10 transition-opacity duration-300 group-hover:opacity-20">
                  <Icon className="h-16 w-16" style={{ color }} />
                </div>
                <span
                  data-testid={`award-icon-${a.callsign}`}
                  className="flex h-12 w-12 items-center justify-center rounded-full border"
                  style={{ borderColor: `${color}66`, backgroundColor: `${color}0D`, boxShadow: `0 0 20px ${glow}` }}
                >
                  <Icon className="h-6 w-6" style={{ color }} />
                </span>
                <h3 className="mt-6 font-display text-xl font-bold text-[var(--aiv-text)]">
                  {a.title}
                </h3>
                <p className="mt-2 text-sm font-semibold" style={{ color }}>{a.name}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-mono-data text-xs text-[var(--aiv-text-2)]">{a.callsign}</span>
                  <span className="text-xs text-[var(--aiv-text-2)]/70">{a.when}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
